if (!customElements.get("bundle-qty-selector")) {
  const formatBundleMoney = (cents, format) =>
    theme.Currency.formatMoney(cents, format).replace(/,(?=\d{3}\b)/g, " ");

  customElements.define(
    "bundle-qty-selector",
    class BundleQtySelector extends HTMLElement {
      connectedCallback() {
        this.componentData = this.getJSON(
          `BundleData-${this.dataset.sectionId}-${this.dataset.productId}`
        );
        this.onOptionChangeListener = this.onOptionChange.bind(this);
        this.onQuantityChangeListener = this.onQuantityChange.bind(this);
        this.onClickListener = this.onClick.bind(this);
        this._productForm = document.getElementById(this.dataset.formId);
        this._submitHandler = this.interceptSubmit.bind(this);

        if (this._productForm) {
          this._productForm.addEventListener("submit", this._submitHandler, {
            capture: true,
          });
        }

        this.addEventListener("change", this.onOptionChangeListener);
        this.addEventListener("input", this.onQuantityChangeListener);
        this.addEventListener("change", this.onQuantityChangeListener);
        this.addEventListener("click", this.onClickListener);

        this.componentItems.forEach((item) => this.syncComponentItem(item));
        this.updateTotals();
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onOptionChangeListener);
        this.removeEventListener("input", this.onQuantityChangeListener);
        this.removeEventListener("change", this.onQuantityChangeListener);
        this.removeEventListener("click", this.onClickListener);
        this._productForm?.removeEventListener("submit", this._submitHandler, {
          capture: true,
        });
      }

      getJSON(id) {
        const el = document.getElementById(id);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch {
          return null;
        }
      }

      get componentItems() {
        return Array.from(this.querySelectorAll("[data-component-index]"));
      }

      get componentEntries() {
        return Array.from(this.querySelectorAll("[data-component-entry]"));
      }

      get productForm() {
        return document.getElementById(this.dataset.formId);
      }

      get submitButton() {
        return this.productForm?.querySelector(
          '[type="submit"][name="add"], [type="submit"].product-form__submit'
        );
      }

      getComponentForItem(item) {
        if (!this.componentData) return null;
        const index = parseInt(item.dataset.componentIndex, 10);
        return this.componentData[index] || null;
      }

      getEntries(item) {
        return Array.from(item.querySelectorAll("[data-component-entry]"));
      }

      onOptionChange(event) {
        if (!event.target.matches("[data-component-option]")) return;
        const item = event.target.closest("[data-component-index]");
        if (!item) return;
        this.syncComponentItem(item);
        this.updateTotals();
        this.updateErrorState();
      }

      onQuantityChange(event) {
        if (!event.target.matches('input[type="number"]')) return;
        this.updateTotals();
        this.updateErrorState();
      }

      onClick(event) {
        const addButton = event.target.closest("[data-component-add]");
        if (addButton) {
          const item = addButton.closest("[data-component-index]");
          if (!item) return;
          this.addComponentEntry(item);
          return;
        }

        const clearButton = event.target.closest("[data-component-clear]");
        if (clearButton) {
          const item = clearButton.closest("[data-component-index]");
          const entry = clearButton.closest("[data-component-entry]");
          if (!item || !entry) return;
          this.clearComponentEntry(item, entry);
          return;
        }

        const button = event.target.closest(".quantity__button");
        if (!button) return;
        setTimeout(() => this.updateTotals(), 0);
      }

      addComponentEntry(item) {
        const entriesContainer = item.querySelector("[data-component-entries]");
        const firstEntry = entriesContainer?.querySelector("[data-component-entry]");
        if (!entriesContainer || !firstEntry) return;

        const clonedEntry = firstEntry.cloneNode(true);
        this.resetEntry(clonedEntry, { rebuildSelect: true });
        entriesContainer.appendChild(clonedEntry);

        this.syncComponentItem(item);
        this.updateTotals();
      }

      clearComponentEntry(item, entry) {
        const entries = this.getEntries(item);
        if (entries.length > 1) {
          entry.remove();
          this.syncComponentItem(item);
          this.updateTotals();
          this.updateErrorState();
          return;
        }

        this.resetEntry(entry, { rebuildSelect: false });
        this.syncComponentItem(item);
        this.updateTotals();
        this.updateErrorState();
      }

      resetEntry(entry, { rebuildSelect = false } = {}) {
        entry.dataset.componentId = "";
        entry.dataset.available = "false";
        entry.dataset.componentState = "idle";
        entry.dataset.price = "";
        entry.dataset.compareAtPrice = "";

        const variantSelect = entry.querySelector("[data-component-variant-select]");
        if (variantSelect) {
          if (rebuildSelect) {
            variantSelect.classList.remove("custom-select-hidden");
            entry
              .querySelectorAll(".custom-select-wrapper")
              .forEach((wrapper) => wrapper.remove());
          }
          variantSelect.value = "";
          if (rebuildSelect) {
            variantSelect.id = `BundleVariant-${this.dataset.sectionId}-${this.dataset.productId}-${Date.now()}-${Math.floor(
              Math.random() * 1000
            )}`;
          }
          variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }

        const soldOutEl = entry.querySelector("[data-component-soldout]");
        if (soldOutEl) soldOutEl.classList.add("hidden");

        const qtyContainer = entry.querySelector("[data-component-qty]");
        const qtyInput = qtyContainer?.querySelector('input[type="number"]');
        if (qtyContainer) qtyContainer.classList.add("bundle-qty__qty--inactive");
        if (qtyInput) {
          qtyInput.value = "1";
          qtyInput.setAttribute("disabled", "disabled");
        }
      }

      syncComponentItem(item) {
        const component = this.getComponentForItem(item);
        if (!component) return;

        this.getEntries(item).forEach((entry) => this.syncEntry(component, entry));
        this.updateEntryActions(item);
      }

      syncEntry(component, entry) {
        const variantSelect = entry.querySelector("[data-component-variant-select]");
        const qtyContainer = entry.querySelector("[data-component-qty]");
        const qtyInput = qtyContainer?.querySelector('input[type="number"]');
        const soldOutEl = entry.querySelector("[data-component-soldout]");
        const selected = Boolean(variantSelect?.value);

        if (!selected) {
          entry.dataset.componentId = "";
          entry.dataset.available = "false";
          entry.dataset.componentState = "idle";
          entry.dataset.price = "";
          entry.dataset.compareAtPrice = "";
          if (qtyContainer) qtyContainer.classList.add("bundle-qty__qty--inactive");
          if (qtyInput) {
            qtyInput.value = "1";
            qtyInput.setAttribute("disabled", "disabled");
          }
          if (soldOutEl) soldOutEl.classList.add("hidden");
          return;
        }

        const matched = component.variants.find(
          (variant) => String(variant.id) === variantSelect.value
        );

        if (!matched || !matched.available) {
          entry.dataset.componentId = "";
          entry.dataset.available = "false";
          entry.dataset.componentState = "sold_out";
          entry.dataset.price = "";
          entry.dataset.compareAtPrice = "";
          if (qtyContainer) qtyContainer.classList.add("bundle-qty__qty--inactive");
          if (qtyInput) {
            qtyInput.value = "1";
            qtyInput.setAttribute("disabled", "disabled");
          }
          if (soldOutEl) soldOutEl.classList.remove("hidden");
          return;
        }

        entry.dataset.componentId = String(matched.id);
        entry.dataset.available = "true";
        entry.dataset.componentState = "available";
        entry.dataset.price = String(matched.price || 0);
        entry.dataset.promoPercent = String(component.promo_percent || 0);
        entry.dataset.compareAtPrice = String(matched.compare_at_price || matched.price || 0);
        if (qtyContainer) qtyContainer.classList.remove("bundle-qty__qty--inactive");
        if (qtyInput) qtyInput.removeAttribute("disabled");
        if (soldOutEl) soldOutEl.classList.add("hidden");
      }

      updateEntryActions(item) {
        const entries = this.getEntries(item);
        const hasMultipleEntries = entries.length > 1;

        entries.forEach((entry) => {
          const removeButton = entry.querySelector("[data-component-clear]");
          const addButton = entry.querySelector("[data-component-add-inline]");
          if (!removeButton) return;

          const state = entry.dataset.componentState || "idle";
          const canShowRemove =
            state !== "sold_out" && (hasMultipleEntries || state === "available");
          const isLastEntry = entries[entries.length - 1] === entry;
          const canShowAdd = state === "available" && isLastEntry;
          removeButton.hidden = !canShowRemove;
          removeButton.classList.toggle("bundle-qty__link--hidden", !canShowRemove);
          if (addButton) {
            addButton.hidden = !canShowAdd;
            addButton.classList.toggle("bundle-qty__link--hidden", !canShowAdd);
          }
        });
      }

      updateTotals() {
        const totalEl = this.querySelector("[data-bundle-total-price]");
        if (!totalEl) return;

        const totalContainer = this.querySelector("[data-bundle-total]");
        const totalCompareEl = this.querySelector("[data-bundle-total-compare]");
        const totalCompareWrapEl = this.querySelector("[data-bundle-total-compare-wrap]");
        const priceWrapper = this.querySelector(".bundle-qty__total .price");
        const hasSelectedVariant = this.componentEntries.some(
          (entry) => entry.dataset.componentState !== "idle"
        );

        let totalPrice = 0;
        let totalCompareAtPrice = 0;
        let promoPrice = 0;
        let hasAvailablePrice = false;

        this.componentEntries.forEach((entry) => {
          const available = entry.dataset.available === "true";
          const price = parseInt(entry.dataset.price || 0, 10);
          const compareAtPrice = parseInt(
            entry.dataset.compareAtPrice || price || 0,
            10
          );
          const qtyInput = entry.querySelector("[data-component-qty] input[type=\"number\"]");
          const parsedQty = parseInt(qtyInput?.value || 1, 10);
          const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;

          if (!available || !Number.isFinite(price)) return;

          totalPrice += price * qty;
          totalCompareAtPrice += compareAtPrice * qty;
          promoPrice += Math.round(price * (100 - (Number(entry.dataset.promoPercent) || 0)) / 100) * qty;
          hasAvailablePrice = true;
        });

        if (!hasSelectedVariant) {
          hasAvailablePrice = Boolean(this.componentData?.length);
          for (const component of this.componentData || []) {
            const available = component.variants.filter((variant) => variant.available);
            if (!available.length) {
              hasAvailablePrice = false;
              break;
            }
            const cheapest = available.reduce((min, variant) => variant.price < min.price ? variant : min);
            totalPrice += cheapest.price;
            totalCompareAtPrice += Math.max(cheapest.compare_at_price || 0, cheapest.price);
            promoPrice += Math.round(cheapest.price * (100 - (component.promo_percent || 0)) / 100);
          }
        }

        const hasCurrencyCode = theme && theme.settings && theme.settings.currencyCodeEnabled;
        const moneyFormat = hasCurrencyCode
          ? theme.settings.moneyWithCurrencyFormat
          : theme.settings.moneyFormat;

        const formattedPrice = formatBundleMoney(totalPrice, moneyFormat);
        totalEl.innerHTML = !hasSelectedVariant && this.dataset.fromPrice
          ? this.dataset.fromPrice.replace('[[price]]', formattedPrice)
          : formattedPrice;

        if (totalCompareEl && totalCompareWrapEl) {
          totalCompareEl.innerHTML = formatBundleMoney(
            totalCompareAtPrice,
            moneyFormat
          );
          const isOnSale = totalCompareAtPrice > totalPrice;
          totalCompareWrapEl.classList.toggle("hidden", !isOnSale);
          if (priceWrapper) priceWrapper.classList.toggle("price--on-sale", isOnSale);
        }

        if (totalContainer) totalContainer.hidden = !hasAvailablePrice;

        this.productForm?.dispatchEvent(new CustomEvent('bundle:price-change', {
          detail: { price: totalPrice, promoPrice, from: !hasSelectedVariant, available: hasAvailablePrice },
        }));

        this.updateBundleAvailability();
      }

      updateBundleAvailability() {
        const hasAvailable = this.componentEntries.some(
          (entry) => entry.dataset.componentState === "available"
        );
        const hasSoldOut = this.componentEntries.some(
          (entry) => entry.dataset.componentState === "sold_out"
        );

        const availabilityEl = document.querySelector(
          `[data-bundle-availability][data-section-id="${this.dataset.sectionId}"][data-product-id="${this.dataset.productId}"]`
        );
        const inStockEl = availabilityEl?.querySelector(
          "[data-bundle-availability-in-stock]"
        );
        const unavailableEl = availabilityEl?.querySelector(
          "[data-bundle-availability-unavailable]"
        );

        if (availabilityEl) {
          const shouldShow = hasAvailable || hasSoldOut;
          availabilityEl.hidden = !shouldShow;
          if (inStockEl) inStockEl.classList.toggle("hidden", !(hasAvailable && !hasSoldOut));
          if (unavailableEl) unavailableEl.classList.toggle("hidden", !hasSoldOut);
        }

        const submitButton = this.submitButton;
        if (!submitButton) return;

        const submitLabelEl = submitButton.querySelector(".btn-text > span");
        const addToCartLabel = this.dataset.addToCartLabel || "Add to cart";
        if (submitLabelEl) submitLabelEl.textContent = addToCartLabel;
      }

      updateErrorState() {
        if (this.buildCartItems().length > 0) this.setError();
      }

      dispatchAjaxError(message) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:error", {
            detail: { errorMessage: message },
          })
        );
      }

      async readErrorMessage(response, fallbackMessage = "Network error") {
        try {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const json = await response.json();
            return json?.description || json?.message || json?.error || fallbackMessage;
          }
          const text = await response.text();
          return text || fallbackMessage;
        } catch {
          return fallbackMessage;
        }
      }

      buildCartItems() {
        const quantitiesById = new Map();

        this.componentEntries.forEach((entry) => {
          const id = parseInt(entry.dataset.componentId || "", 10);
          const available = entry.dataset.available === "true";
          const qtyInput = entry.querySelector("[data-component-qty] input[type=\"number\"]");
          const qty = parseInt(qtyInput?.value || "1", 10);

          if (!id || !available || !Number.isFinite(qty) || qty < 1) return;
          quantitiesById.set(id, (quantitiesById.get(id) || 0) + qty);
        });

        return Array.from(quantitiesById.entries()).map(([id, quantity]) => ({
          id,
          quantity,
        }));
      }

      interceptSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }

      async submit() {
        const btn = this.submitButton;
        if (btn?.hasAttribute("aria-disabled")) return;

        const cartItems = this.buildCartItems();
        const hasAvailableSelection = this.componentEntries.some(
          (entry) => entry.dataset.componentState === "available"
        );
        const hasUnavailableSelection = this.componentEntries.some(
          (entry) => entry.dataset.componentState === "sold_out"
        );

        if (cartItems.length === 0) {
          if (hasUnavailableSelection && !hasAvailableSelection) {
            this.setError(
              this.dataset.someUnavailableError ||
                "Some selected variants are unavailable."
            );
          } else {
            this.setError(
              this.dataset.errorSelectOne || "Please select at least one variant."
            );
          }
          return;
        }

        this.setError();

        if (btn) {
          btn.setAttribute("aria-disabled", "true");
          btn.setAttribute("aria-busy", "true");
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        try {
          const response = await fetch(theme.routes.cart_add_url, {
            ...theme.utils.fetchConfig("javascript"),
            body: JSON.stringify({
              items: cartItems,
              sections: sectionsToBundle,
              sections_url: window.location.pathname,
            }),
          });

          if (!response.ok) {
            const message = await this.readErrorMessage(response, "Network error");
            this.setError(message);
            this.dispatchAjaxError(message);
            return;
          }

          let parsedState;
          try {
            parsedState = await response.json();
          } catch {
            const message = "Network error";
            this.setError(message);
            this.dispatchAjaxError(message);
            return;
          }

          if (parsedState.status) {
            this.setError(parsedState.description);
            this.dispatchAjaxError(parsedState.description);
            return;
          }

          const cartResponse = await fetch(theme.routes.cart_url, {
            ...theme.utils.fetchConfig("json", "GET"),
          });
          if (!cartResponse.ok) {
            const message = await this.readErrorMessage(cartResponse, "Network error");
            this.setError(message);
            this.dispatchAjaxError(message);
            return;
          }

          let cartJson;
          try {
            cartJson = await cartResponse.json();
          } catch {
            const message = "Network error";
            this.setError(message);
            this.dispatchAjaxError(message);
            return;
          }

          cartJson.sections = parsedState.sections;

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: "bundle-qty-selector",
            cart: cartJson,
          });

          document.dispatchEvent(
            new CustomEvent("ajaxProduct:added", { detail: { product: parsedState } })
          );

          document.querySelector("cart-drawer")?.show(btn);
        } catch (error) {
          console.error("[BundleQtySelector]", error);
          const message = error?.message || "Fetch failed";
          this.setError(message);
          this.dispatchAjaxError(message);
        } finally {
          if (btn) {
            btn.removeAttribute("aria-busy");
            btn.removeAttribute("aria-disabled");
          }
        }
      }

      setError(message = false) {
        const errorEl = this.productForm?.querySelector(".product-form__error-message");
        if (!errorEl) return;
        errorEl.toggleAttribute("hidden", !message);
        if (message) errorEl.textContent = message;
      }
    }
  );
}
